import React from 'react';
import moment from 'moment';
import Utility from '../../../../Utility/Utility';

const parseJson = (val, fallback) => {
    if (typeof val !== 'string') return val ?? fallback;
    try { return JSON.parse(val); } catch (e) { return fallback; }
};

// Subtle icon components for clean visual accents
const Icons = {
    Calendar: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: '6px' }}>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
    ),
    User: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: '6px' }}>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    ),
    Receipt: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: '6px' }}>
            <polyline points="20 12 20 22 4 22 4 2" />
            <path d="M4 2h16l-3 4 3 4-3 4 3 4-3 4" />
            <line x1="8" y1="7" x2="16" y2="7" />
            <line x1="8" y1="11" x2="14" y2="11" />
            <line x1="8" y1="15" x2="16" y2="15" />
        </svg>
    ),
    MapPin: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: '5px' }}>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
        </svg>
    ),
    Compass: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: '6px' }}>
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
        </svg>
    ),
    CheckCircle: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', flexShrink: 0 }}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    ),
    XCircle: () => (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
    ),
    ShieldAlert: () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: '6px' }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    )
};

const getStatusBadgeStyle = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('confirm') || s.includes('success') || s.includes('booked') || s.includes('complete')) {
        return { background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' };
    }
    if (s.includes('cancel') || s.includes('reject') || s.includes('failed')) {
        return { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' };
    }
    if (s.includes('pending') || s.includes('wait') || s.includes('progress') || s.includes('hold')) {
        return { background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' };
    }
    return { background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' };
};

function Field({ label, value, col = 'col-md-3 col-sm-6' }) {
    if (value === undefined || value === null || value === '') return null;
    return (
        <div className={`${col} mb-2`}>
            <div style={{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#64748b', fontWeight: 600, marginBottom: '1px' }}>
                {label}
            </div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', wordBreak: 'break-word', lineHeight: 1.35 }}>
                {value}
            </div>
        </div>
    );
}

function TourBookingDetails({ viewDetailsData }) {
    const data = viewDetailsData || {};
    if (!data || Object.keys(data).length === 0) {
        return (
            <div className="text-center p-4" style={{ background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>No booking details found</div>
            </div>
        );
    }

    const extracted = parseJson(data.template_extracted_data, {}) || {};
    const priceDetails = parseJson(data.price_distribution, null) || parseJson(data.all_price_details, {}) || {};
    const childList = parseJson(data.child, []) || [];
    const tour = data.tour_details || extracted.tour_details || null;
    const hotel = extracted.hotel_details || null;
    const room = extracted.room_details || null;
    const meal = extracted.meal_plan_details || null;
    const itinerary = parseJson(extracted.itinerary_json, []) || [];
    const inclusions = (extracted.inclusions || []).filter(i => i.is_active !== false);
    const exclusions = (extracted.exclusions || []).filter(i => i.is_active !== false);
    const cancellation = extracted.cancellation_policy;
    const isTour = String(data.template_type || '').toLowerCase() !== 'hotel';
    const currency = Utility.getCurrencySymbol(tour?.base_currency || hotel?.currency || 'INR') || '';
    const money = (v) => `${currency} ${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const fmtDate = (d) => (d && moment(d).isValid() ? moment(d).format('DD MMM YYYY') : '');
    const gst = extracted.gst_breakup || {};
    const title = isTour ? (tour?.template_name || tour?.tour_code || 'Tour') : (hotel?.property_name || 'Hotel');
    const gstRows = ['CGST', 'SGST', 'IGST'].filter(k => gst[k] && (Number(gst[k].amount) > 0 || Number(gst[k].percentage) > 0));

    const cardStyle = {
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '10px 12px',
        marginBottom: '8px'
    };

    const sectionTitleStyle = {
        fontSize: '11.5px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: '#475569',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        paddingBottom: '6px',
        borderBottom: '1px solid #f1f5f9'
    };

    const statusBadgeStyle = getStatusBadgeStyle(data.book_status);

    return (
        <div className="tour-booking-details" style={{
            fontSize: '12px',
            color: '#1e293b',
            maxHeight: '68vh',
            overflowY: 'auto',
            paddingRight: '4px'
        }}>
            {/* Header Hero Card */}
            <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                color: '#fff',
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '10px'
            }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <span style={{
                                fontSize: '10px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.6px',
                                background: 'rgba(255, 255, 255, 0.15)',
                                padding: '1px 6px',
                                borderRadius: '3px',
                                fontWeight: 600,
                                color: '#e2e8f0'
                            }}>
                                {data.template_type || (isTour ? 'Tour Package' : 'Hotel Booking')}
                            </span>
                            {data.booking_number && (
                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                    #{data.booking_number}
                                </span>
                            )}
                        </div>
                        <h4 style={{ fontSize: '15px', fontWeight: 700, margin: '2px 0', color: '#ffffff', letterSpacing: '-0.2px' }}>
                            {title}
                        </h4>
                        {isTour && (tour?.from_location_name || tour?.to_location_name) && (
                            <div style={{ fontSize: '11.5px', color: '#cbd5e1', display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                                <Icons.MapPin />
                                <span>{tour?.from_location_name} &rarr; {tour?.to_location_name}</span>
                            </div>
                        )}
                        {!isTour && hotel && (
                            <div style={{ fontSize: '11.5px', color: '#cbd5e1', display: 'flex', alignItems: 'center', marginTop: '2px' }}>
                                <Icons.MapPin />
                                <span>{[hotel.address, hotel.city, hotel.state].filter(Boolean).join(', ')}</span>
                            </div>
                        )}
                    </div>
                    {data.book_status && (
                        <span style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '4px 10px',
                            borderRadius: '16px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            ...statusBadgeStyle
                        }}>
                            {data.book_status}
                        </span>
                    )}
                </div>
            </div>

            {/* Booking & Stay Overview */}
            <div style={cardStyle}>
                <div style={sectionTitleStyle}>
                    <Icons.Calendar /> Booking & Schedule Details
                </div>
                <div className="row g-2">
                    <Field label="Booking Number" value={data.booking_number} />
                    <Field label="Quotation Number" value={data.quotation_number} />
                    <Field label="Booking Type" value={data.template_type} />
                    <Field label="Booked On" value={fmtDate(data.createdAt)} />
                    <Field label={isTour ? 'Travel From' : 'Check-in'} value={fmtDate(data.quoted_from)} />
                    <Field label={isTour ? 'Travel To' : 'Check-out'} value={fmtDate(data.quoted_to)} />
                    {isTour && <Field label="Duration" value={tour?.duration || extracted.duration} />}
                    {isTour && <Field label="Tour Scope" value={tour?.tour_scope} />}
                    {isTour && <Field label="Category" value={tour?.package_category} />}
                    <Field label="Adults" value={data.adult ? `${data.adult} Adult${Number(data.adult) > 1 ? 's' : ''}` : null} />
                    <Field label="Children" value={childList.length > 0 ? `${childList.length} (Age: ${childList.map(c => c.age).join(', ')})` : '0'} />
                    {!isTour && <Field label="Room Type" value={room?.room_type_name} />}
                    {!isTour && <Field label="Meal Plan" value={meal?.plan_name} />}
                    {!isTour && <Field label="Nights" value={extracted.nights} />}
                    <Field label="Valid Until" value={fmtDate(data.valid_until)} />
                </div>
                {isTour && tour?.tour_description && (
                    <div style={{
                        marginTop: '6px',
                        padding: '8px 10px',
                        background: '#f8fafc',
                        borderRadius: '6px',
                        borderLeft: '3px solid #64748b',
                        fontSize: '11.5px',
                        color: '#475569',
                        lineHeight: 1.4
                    }}>
                        {tour.tour_description}
                    </div>
                )}
            </div>

            {/* Customer Details */}
            <div style={cardStyle}>
                <div style={sectionTitleStyle}>
                    <Icons.User /> Customer Information
                </div>
                <div className="row g-2">
                    <Field label="Name" value={data.customer_name} />
                    <Field label="Phone" value={data.customer_phone} />
                    <Field label="Email" value={data.customer_email} />
                    <Field label="GSTIN" value={data.customer_gstin_no} />
                    <Field label="Company" value={data.company_name} />
                    <Field label="Company Address" value={data.company_address} col="col-md-6 col-sm-12" />
                </div>
            </div>

            {/* Price Details */}
            <div style={cardStyle}>
                <div style={sectionTitleStyle}>
                    <Icons.Receipt /> Price Breakdown
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 12px', border: '1px solid #edf2f7' }}>
                    {isTour && extracted.adult_price != null && (
                        <>
                            <div className="d-flex justify-content-between py-1 border-bottom border-light" style={{ fontSize: '12px' }}>
                                <span className="text-secondary">Adult ({data.adult} &times; {money(extracted.adult_price)})</span>
                                <span className="fw-semibold text-dark">{money(extracted.adult_total_price)}</span>
                            </div>
                            {childList.length > 0 && (
                                <div className="d-flex justify-content-between py-1 border-bottom border-light" style={{ fontSize: '12px' }}>
                                    <span className="text-secondary">Child ({extracted.chargeable_child ?? childList.length} &times; {money(extracted.child_price)})</span>
                                    <span className="fw-semibold text-dark">{money(extracted.child_total_price)}</span>
                                </div>
                            )}
                        </>
                    )}
                    <div className="d-flex justify-content-between py-1" style={{ fontSize: '12px' }}>
                        <span className="text-secondary">Base Price</span>
                        <span className="fw-semibold text-dark">{money(priceDetails.base_price)}</span>
                    </div>
                    {Number(priceDetails.markup_total_amount) > 0 && (
                        <div className="d-flex justify-content-between py-1" style={{ fontSize: '12px' }}>
                            <span className="text-secondary">Markup</span>
                            <span className="fw-semibold text-dark">{money(priceDetails.markup_total_amount)}</span>
                        </div>
                    )}
                    {Number(priceDetails.discount_amount) > 0 && (
                        <div className="d-flex justify-content-between py-1 text-success" style={{ fontSize: '12px' }}>
                            <span>Discount</span>
                            <span className="fw-semibold">- {money(priceDetails.discount_amount)}</span>
                        </div>
                    )}
                    <div className="d-flex justify-content-between py-1" style={{ fontSize: '12px' }}>
                        <span className="text-secondary">Taxable Amount</span>
                        <span className="fw-semibold text-dark">{money(priceDetails.taxable_amount)}</span>
                    </div>
                    {gstRows.map(k => (
                        <div className="d-flex justify-content-between py-1" key={k} style={{ fontSize: '12px' }}>
                            <span className="text-secondary">{k} ({gst[k].percentage}%)</span>
                            <span className="fw-semibold text-dark">{money(gst[k].amount)}</span>
                        </div>
                    ))}
                    {Number(priceDetails.round_off_amount) !== 0 && (
                        <div className="d-flex justify-content-between py-1" style={{ fontSize: '12px' }}>
                            <span className="text-secondary">Round Off</span>
                            <span className="fw-semibold text-dark">{money(priceDetails.round_off_amount)}</span>
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-2 pt-2" style={{ borderTop: '1px dashed #e2e8f0' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>Total Amount</span>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>{money(data.total_amount)}</span>
                </div>
            </div>

            {/* Itinerary */}
            {isTour && itinerary.length > 0 && (
                <div style={cardStyle}>
                    <div style={sectionTitleStyle}>
                        <Icons.Compass /> Tour Itinerary
                    </div>
                    <div style={{ position: 'relative', paddingLeft: '12px', borderLeft: '2px solid #e2e8f0', marginLeft: '4px' }}>
                        {itinerary.map((day, di) => (
                            <div key={di} className="mb-3" style={{ position: 'relative' }}>
                                <div style={{
                                    position: 'absolute',
                                    left: '-18px',
                                    top: '3px',
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: '#2563eb',
                                    border: '2px solid #ffffff',
                                    boxShadow: '0 0 0 1.5px #93c5fd'
                                }} />
                                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>
                                    <span style={{
                                        background: '#eff6ff',
                                        color: '#1d4ed8',
                                        padding: '1px 6px',
                                        borderRadius: '3px',
                                        fontSize: '10.5px',
                                        fontWeight: 700,
                                        marginRight: '6px'
                                    }}>
                                        DAY {day.day_number}
                                    </span>
                                    {day.title}
                                </div>
                                {day.description && (
                                    <div className="text-secondary mt-1 mb-1" style={{ fontSize: '11.5px', lineHeight: 1.4 }}>
                                        {day.description}
                                    </div>
                                )}
                                {(day.items || []).filter(it => it.is_active !== false).length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                                        {(day.items || []).filter(it => it.is_active !== false).map((it, ii) => (
                                            <div key={it.id || ii} style={{
                                                background: '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '6px',
                                                padding: '6px 10px',
                                                fontSize: '11.5px'
                                            }}>
                                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
                                                    <div>
                                                        <strong style={{ color: '#0f172a' }}>{it.title}</strong>
                                                        {it.duration && <span className="text-muted ms-1" style={{ fontSize: '10.5px' }}>({it.duration})</span>}
                                                    </div>
                                                    {it.type && (
                                                        <span style={{
                                                            fontSize: '10px',
                                                            background: '#e0f2fe',
                                                            color: '#0369a1',
                                                            padding: '1px 5px',
                                                            borderRadius: '3px',
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            {it.type}
                                                        </span>
                                                    )}
                                                </div>
                                                {it.description && <div className="text-secondary mt-1" style={{ fontSize: '11px' }}>{it.description}</div>}
                                                {it.notes && <div className="fst-italic text-muted mt-1" style={{ fontSize: '10.5px' }}>Note: {it.notes}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Inclusions & Exclusions */}
            {(inclusions.length > 0 || exclusions.length > 0) && (
                <div style={cardStyle}>
                    <div className="row g-2">
                        {inclusions.length > 0 && (
                            <div className={exclusions.length > 0 ? "col-md-6" : "col-12"}>
                                <div style={{ ...sectionTitleStyle, color: '#047857', marginBottom: '8px' }}>
                                    Inclusions
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {inclusions.map((i, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            fontSize: '11.5px',
                                            color: '#334155',
                                            lineHeight: 1.35
                                        }}>
                                            <Icons.CheckCircle />
                                            <span>{i.inclusion_text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {exclusions.length > 0 && (
                            <div className={inclusions.length > 0 ? "col-md-6" : "col-12"}>
                                <div style={{ ...sectionTitleStyle, color: '#b91c1c', marginBottom: '8px' }}>
                                    Exclusions
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {exclusions.map((e, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            fontSize: '11.5px',
                                            color: '#334155',
                                            lineHeight: 1.35
                                        }}>
                                            <Icons.XCircle />
                                            <span>{e.exclusion_text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Cancellation Policy */}
            {cancellation && Array.isArray(cancellation.rules) && cancellation.rules.length > 0 && (
                <div style={cardStyle}>
                    <div style={sectionTitleStyle}>
                        <Icons.ShieldAlert /> Cancellation Policy{cancellation.policy_name ? `: ${cancellation.policy_name}` : ''}
                    </div>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0" style={{ fontSize: '11.5px', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                            <thead style={{ background: '#f8fafc', color: '#475569', textTransform: 'uppercase', fontSize: '10.5px', letterSpacing: '0.4px' }}>
                                <tr>
                                    <th className="py-1 px-2">Days Before Travel</th>
                                    <th className="py-1 px-2">Refund</th>
                                    <th className="py-1 px-2">Cancellation Charge</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cancellation.rules.map((r, i) => (
                                    <tr key={i} style={{ borderBottom: i === cancellation.rules.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                                        <td className="py-1 px-2 fw-medium text-dark">{r.days_before_travel_from} - {r.days_before_travel_to} Days</td>
                                        <td className="py-1 px-2">
                                            <span style={{ background: '#ecfdf5', color: '#047857', padding: '2px 6px', borderRadius: '3px', fontWeight: 600 }}>
                                                {r.refund_percentage}%
                                            </span>
                                        </td>
                                        <td className="py-1 px-2">
                                            <span style={{ background: '#fef2f2', color: '#b91c1c', padding: '2px 6px', borderRadius: '3px', fontWeight: 600 }}>
                                                {r.cancellation_charge_percentage}%
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TourBookingDetails;
